type JoinOnDelimiter<
  A extends string,
  B extends string,
  D extends ' ' | '_' | '-',
  Strip extends boolean,
> = Strip extends true
  ? `${TitleCase<A, Strip>}${TitleCase<B, Strip>}`
  : `${TitleCase<A, Strip>}${D}${TitleCase<B, Strip>}`;

/**
 * Represents the title case form of the string defined by the generic type parameter {@link S}.
 *
 * The generic type parameter {@link Strip} represents whether or not the string should be stripped
 * of any delimiters (" ", "_", or "-") when converting the string to title case.
 */
export type TitleCase<
  S extends string,
  Strip extends boolean = true,
> = S extends `${infer A}-${infer B}`
  ? JoinOnDelimiter<A, B, '-', Strip>
  : S extends `${infer A} ${infer B}`
    ? JoinOnDelimiter<A, B, ' ', Strip>
    : S extends `${infer A}_${infer B}`
      ? JoinOnDelimiter<A, B, '_', Strip>
      : Capitalize<Lowercase<S>>;

/**
 * Removes delimiters, ' ', '_', or '-' from the provided string.
 */
const stripDelimiters = (value: string) => value.replace(/[\s-_/]/g, '');

/**
 * A regular expression that splits a string on all sequences of delimiters, ' ', '_', or '-'.
 * This regular expression is used to determine what characters to apply the capitalization to.
 */
const SplitStringOnDelimitersRegex = /(?:^|[\s-_/]+)\w/g;

/**
 * Converts the provided string, {@link T}, into a title case form.
 *
 * @param {string} value
 *   The string that should be converted to title case form.
 * @param {boolean} strip
 *   Whether or not the delimiters that separate the alphanumeric sub-strings in the provided string
 *   should be left in the string that is returned by the method.  If 'true', the delimiters will
 *   be stripped from the string, resulting in a title case form of the string that has no
 *   delimiters.
 *
 * @example
 * // Returns 'TheFoxJumpedOverTheBush'
 * toTitleCase('the_ -fox_jumped__over   the bush', true)
 * @example
 * // Returns 'The_Fox_ Jumped__Overr  The Bush'
 * toTitleCase('The_fox_ jumped__overR  THE bush', false)
 *
 * @returns {string} The title case form of the provided string.
 */
export const titleCase = <T extends string, Strip extends boolean>(
  value: T,
  strip: Strip,
): TitleCase<T, Strip> => {
  const titleForm = value
    .toLowerCase()
    .replaceAll(SplitStringOnDelimitersRegex, match => match.toUpperCase());
  return (strip ? stripDelimiters(titleForm) : titleForm) as TitleCase<T, Strip>;
};
